import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GitBranch, Plus, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const field = "h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";

const StaffPicker = ({ options, value, onChange, testid }) => (
  <div data-testid={testid} className="flex flex-wrap gap-1.5">
    {options.map((s) => {
      const on = value.includes(s.id);
      return (
        <button key={s.id} type="button" data-testid={`${testid}-${s.id}`} onClick={() => onChange(on ? value.filter((x) => x !== s.id) : [...value, s.id])}
          className={`h-7 px-2.5 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-input text-muted-foreground hover:bg-muted"}`}>{s.name || s.email}</button>
      );
    })}
  </div>
);

const BranchRow = ({ b, data, onSaved }) => {
  const [edit, setEdit] = useState(null);
  const names = data.staff_options.filter((s) => b.staff.includes(s.id)).map((s) => s.name || s.email);
  const save = async () => {
    try { await api.put(`/branches/${b.id}`, edit); toast.success("Branch updated"); setEdit(null); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (edit) return (
    <div data-testid={`branch-edit-${b.id}`} className="border-t border-border py-3 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input data-testid="branch-edit-name" className={field} value={edit.branch_name} onChange={(e) => setEdit({ ...edit, branch_name: e.target.value })} />
        <input data-testid="branch-edit-address" className={field} placeholder="Address / location" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} />
        {!b.is_main && <input data-testid="branch-edit-prefix" className={field} placeholder="Invoice prefix" value={edit.invoice_prefix} onChange={(e) => setEdit({ ...edit, invoice_prefix: e.target.value })} />}
      </div>
      {!b.is_main && <><div className="text-xs text-muted-foreground">Staff who can open this branch</div>
        <StaffPicker testid="branch-edit-staff" options={data.staff_options} value={edit.staff_ids} onChange={(v) => setEdit({ ...edit, staff_ids: v })} /></>}
      <div className="flex gap-2">
        <button data-testid="branch-edit-save" onClick={save} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs flex items-center gap-1"><Check size={13} /> Save</button>
        <button data-testid="branch-edit-cancel" onClick={() => setEdit(null)} className="h-8 px-3 rounded-md border border-input text-xs flex items-center gap-1"><X size={13} /> Cancel</button>
      </div>
    </div>
  );
  return (
    <div data-testid={`branch-row-${b.id}`} className="border-t border-border py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <div className="font-medium">{b.branch_name}{b.is_main && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-muted">Main</span>}</div>
        <div className="text-xs text-muted-foreground">{[b.address, b.invoice_prefix && `Invoice prefix ${b.invoice_prefix}`, !b.is_main && `${names.length} staff`].filter(Boolean).join(" · ")}</div>
      </div>
      {data.can_manage && <button data-testid={`branch-edit-btn-${b.id}`} onClick={() => setEdit({ branch_name: b.branch_name, address: b.address, invoice_prefix: b.invoice_prefix, staff_ids: b.staff })}
        className="h-8 px-2.5 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted"><Pencil size={12} /> Edit</button>}
    </div>
  );
};

export const BranchesManager = () => {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ branch_name: "", address: "", invoice_prefix: "", staff_ids: [] });
  const load = () => api.get("/branches").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const add = async () => {
    if (!form.branch_name.trim()) return toast.error("Enter a branch name");
    try {
      await api.post("/branches", form);
      toast.success("Branch added — switch to it from the company menu");
      setForm({ branch_name: "", address: "", invoice_prefix: "", staff_ids: [] });
      load();
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="branches-manager" className="rounded-xl border border-border bg-card p-5">
      <h3 className="font-display text-lg flex items-center gap-2 mb-1"><GitBranch size={17} /> Branches & locations</h3>
      <p className="text-xs text-muted-foreground mb-3">Each branch has its own cash book, opening cash, daily closing, day lock and invoice/voucher/receipt numbering. Switch between branches from the company menu. Partners and bank accounts are shared.</p>
      {data.branches.map((b) => <BranchRow key={b.id} b={b} data={data} onSaved={load} />)}
      {data.can_manage && (
        <div className="border-t border-border pt-3 mt-1 space-y-2">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Add branch</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input data-testid="branch-new-name" className={field} placeholder="Branch name (e.g. Ajmer Road)" value={form.branch_name} onChange={(e) => setForm({ ...form, branch_name: e.target.value })} />
            <input data-testid="branch-new-address" className={field} placeholder="Address / location" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <input data-testid="branch-new-prefix" className={field} placeholder="Invoice prefix (auto if empty)" value={form.invoice_prefix} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} />
          </div>
          {data.staff_options.length > 0 && <><div className="text-xs text-muted-foreground">Staff who can open this branch (you are added automatically)</div>
            <StaffPicker testid="branch-new-staff" options={data.staff_options} value={form.staff_ids} onChange={(v) => setForm({ ...form, staff_ids: v })} /></>}
          <button data-testid="branch-add" onClick={add} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-1.5"><Plus size={15} /> Add branch</button>
        </div>
      )}
    </div>
  );
};
